/**
 * Activity / Dashboard - Phase 3 wiring.
 *
 * Live data source (release 2.1 item 4):
 *   GET /v1/activity/summary - events in the last hour, blocked in the last
 *   day, per-minute QPS and the top actors (ClickHouse aggregates over every
 *   event), plus UEBA findings and early-warning fires in the last day
 *   (Postgres counts over every row).
 *
 * This page used to count the 1,000 newest events and the 200-row UEBA and
 * early-warning lists in the browser, so its numbers stopped at those sizes.
 */

import { useEffect, useMemo, useState } from 'react';
import { Activity as ActivityIcon, TrendingUp, Waves, Radio, Timer, AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { motion } from 'motion/react';
import { getActivitySummary } from '@/lib/api';
import { ActivityStoreNotice } from '@/components/activity/ActivityStoreNotice';

interface ActorRow {
  actor: string;
  queries: number;
  blocked: number;
  sev: 'high' | 'medium' | 'low';
}

export function ActivityDashboard() {
  const [recentAlerts, setRecentAlerts] = useState(0);
  const [pwaveFires, setPwaveFires] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Every number here is counted by the server over every event and finding
  // (release 2.1 item 4). The page used to count the 1,000 newest events and
  // the 200-row UEBA / early-warning lists.
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof getActivitySummary>> | null>(null);

  const refresh = (signal?: AbortSignal) =>
    getActivitySummary()
      .then((res) => {
        if (signal?.aborted) return;
        setSummary(res);
        setRecentAlerts(res.uebaFindings);
        setPwaveFires(res.earlyWarning24h);
        // A deployment without an activity store is not an error: the KPIs say
        // "Not available" and the notice says why. Any other unread is.
        setError(
          res.events || res.activityStore?.reason
            ? null
            : 'events: the activity store could not be read',
        );
      })
      .catch((err) => {
        if (signal?.aborted) return;
        setError(`summary: ${(err as Error)?.message ?? 'failed'}`);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const storeAbsent = summary?.activityStore?.available === false && summary.activityStore.reason
    ? summary.activityStore
    : null;

  // KPI calculations (server counts; 0 while unknown, with the error banner).
  const queriesLastHour = summary?.events?.eventsLastHour ?? 0;
  const blocked24h = summary?.events?.blocked24h ?? 0;

  const qpsSeries = useMemo(
    () =>
      (summary?.events?.perMinute ?? Array.from({ length: 60 }, () => ({ qps: 0, blocked: 0 }))).map(
        (b, i) => ({ t: `${59 - i}m`, qps: b.qps, blocked: b.blocked }),
      ),
    [summary],
  );
  const topActors = useMemo<ActorRow[]>(
    () =>
      (summary?.events?.topActors24h ?? []).map((a) => {
        const ratio = a.queries > 0 ? a.blocked / a.queries : 0;
        const sev: ActorRow['sev'] = ratio > 0.05 ? 'high' : ratio > 0.01 ? 'medium' : 'low';
        return { actor: a.actor, queries: a.queries, blocked: a.blocked, sev };
      }),
    [summary],
  );
  const topQuery = Math.max(1, ...topActors.map((a) => a.queries));

  return (
    <PageContainer>
      <PageHeader
        title="Activity"
        subtitle="Database Activity Monitoring with statement hashing, UEBA baselines, Submarine Sonar, and the only Consent-Aware Query Firewall in India."
        badges={[
          { label: 'DAM', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : storeAbsent
                ? [{ label: 'Query activity not available', variant: 'glass' as const }]
                : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={<Button size="sm" asChild><Link to="/activity/stream">Open live stream</Link></Button>}
      />

      {loading && <LoadingState caption="Loading Activity dashboard…" />}
      {error && !loading && (
        <ErrorState
          title="Some Activity data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          {storeAbsent && <ActivityStoreNotice status={storeAbsent} what="Query activity (events, blocks, top actors)" />}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Queries / hour"
              value={queriesLastHour}
              {...(storeAbsent ? { unavailable: 'Not available' } : {})}
              icon={ActivityIcon}
              hint="from last 1h of events"
            />
            <KpiCard
              label="Queries blocked (24h)"
              value={blocked24h}
              {...(storeAbsent ? { unavailable: 'Not available' } : {})}
              icon={AlertTriangle}
              glow={blocked24h > 0 ? 'medium' : 'low'}
              hint="decision = block"
            />
            <KpiCard
              label="UEBA alerts"
              value={recentAlerts}
              icon={TrendingUp}
              glow={recentAlerts > 0 ? 'high' : 'low'}
              hint="open findings"
            />
            <KpiCard
              label="P-Wave fires (24h)"
              value={pwaveFires}
              icon={Radio}
              glow={pwaveFires > 0 ? 'high' : 'low'}
              hint="early-warning indicators"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GlassCard className="lg:col-span-2 p-6">
              <CardHeader className="p-0 mb-4 flex-row items-center justify-between">
                <div>
                  <CardTitle>Live query throughput · last 60 minutes</CardTitle>
                  <CardDescription clamp>QPS vs blocked QPS, by minute, over every event of the last hour</CardDescription>
                </div>
                <Badge variant="glass" className="gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                  Live
                </Badge>
              </CardHeader>
              {queriesLastHour === 0 && topActors.length === 0 ? (
                <div className="text-xs text-muted-foreground py-12 text-center">
                  No activity events on file yet. Connect a database, run a scan, or wait for the live stream to start populating.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={qpsSeries}>
                    <defs>
                      <linearGradient id="g-qps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--chart-2))" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="g-blk" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--sev-critical))" stopOpacity={0.85} />
                        <stop offset="100%" stopColor="hsl(var(--sev-critical))" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis dataKey="t" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <YAxis yAxisId="l" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <YAxis yAxisId="r" orientation="right" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    <Area yAxisId="l" type="monotone" dataKey="qps" stroke="hsl(var(--chart-2))" fill="url(#g-qps)" strokeWidth={2} />
                    <Area yAxisId="r" type="monotone" dataKey="blocked" stroke="hsl(var(--sev-critical))" fill="url(#g-blk)" strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Top actors</CardTitle>
                <CardDescription clamp>By query count in the recent window. Severity by block ratio.</CardDescription>
              </CardHeader>
              {topActors.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center">
                  No actors observed yet.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {topActors.map((a, i) => (
                    <motion.div
                      key={a.actor}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <code className="text-[11px] font-mono truncate max-w-[60%]">{a.actor}</code>
                        <span className="text-xs tabular">{a.queries.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(a.queries / topQuery) * 100}%` }}
                          transition={{ delay: 0.2 + i * 0.05, duration: 0.7 }}
                          className={`h-full bg-gradient-to-r ${a.sev === 'high' ? 'from-sev-critical to-sev-high' : a.sev === 'medium' ? 'from-sev-medium to-sev-low' : 'from-chart-2 to-chart-6'}`}
                        />
                      </div>
                      {a.blocked > 0 && (
                        <div className="text-[10px] text-sev-critical mt-1">{a.blocked} blocked</div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <QuickLink to="/activity/stream" icon={ActivityIcon} label="Live Stream" />
            <QuickLink to="/activity/ueba" icon={TrendingUp} label="UEBA" />
            <QuickLink to="/activity/sonar" icon={Waves} label="Sonar" />
            <QuickLink to="/activity/p-wave" icon={Radio} label="P-Wave" />
            <QuickLink to="/activity/breach-readiness" icon={Timer} label="Breach 6h" />
          </div>
        </>
      )}
    </PageContainer>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <Link to={to}>
      <GlassCard className="p-4 flex flex-col items-center gap-2 lift cursor-pointer">
        <Icon className="h-5 w-5 text-primary" />
        <span className="text-xs font-medium text-center">{label}</span>
      </GlassCard>
    </Link>
  );
}
